import React from 'react';
import PropTypes from 'prop-types';
import { Form, DatePicker, Button, Select, Checkbox, message, Radio, Row, Col, Modal } from 'antd';
import moment from 'moment';
import { jsonRequest } from '../../../helpers';
import PartnerPicker from './PartnerPicker';
import ValuePicker from './ValuePicker';
import { ColumnTitle, DividedColumn, EmptyHint, SummaryBox, SummaryRow, FooterRow } from './style';
import _ from 'lodash';

const { Option } = Select;

// แยก Report = 1 คำขอต่อ 1 บริษัท ยิงพร้อมกันทั้งหมดไม่ได้ (เลือกทีเดียวได้เกือบ 100 บริษัท)
const CONCURRENCY = 4
// เกินเท่านี้ให้ยืนยันก่อน เพราะเอกสารที่สร้างแล้วต้องไล่ยกเลิกทีละใบ
const CONFIRM_FROM = 10

const disabledFutureDate = (current) => current && current > moment().endOf('day')

const ReportForm = ({ lenders , setIsModalOpen , refetch }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [salaryDateOptions, setSalaryDateOptions] = React.useState([])
  const [employmentStatusOptions, setEmploymentStatusOptions] = React.useState([])
  const [partners, setPartners] = React.useState([])

  // ติ๊ก partner ทีละตัวได้ง่ายกว่าเดิม จึงหน่วงไว้ก่อนยิง เพื่อไม่ให้ 1 คลิก = 1 request
  const reloadOptions = React.useMemo(() => _.debounce(() => {
    requestOptions().then(data => {
      if (data) {
        // Automatically select all salary_dates and employment_statuses when partner_ids change
        form.setFieldsValue({
          salary_dates: data.salary_dates,
          employment_statuses: data.employment_statuses
        });
      }
    })
  }, 400), [])

  const onChange = () => {
    const { partner_ids, lender_id } = form.getFieldsValue(true)

    if (lender_id) {
      const lender = _.find(lenders, { id: lender_id });
      setPartners(_.get(lender, 'partners', []))
    }

    // If no partners selected, clear the salary_dates and employment_statuses
    if (!partner_ids || partner_ids.length === 0) {
      reloadOptions.cancel()
      setLoading(false)
      form.setFieldsValue({ salary_dates: [], employment_statuses: [] });
      setSalaryDateOptions([]);
      setEmploymentStatusOptions([]);
    } else if (lender_id) {
      setLoading(true)
      reloadOptions()
    }
  };

  const resetPartners = () => {
    form.setFieldsValue({ partner_ids: []});
  }

  const resetOptions = () => {
    form.setFieldsValue({ salary_dates: [], employment_statuses: []});
  }

  const requestSubmit = async (values) => {
    const authenticity_token = document.head.querySelector(
      'meta[name="csrf-token"]'
    ).content;
    const response = await jsonRequest('POST', '/admins/invoice/invoice_group', { ...values, authenticity_token});

    return response.status === 200
  }

  // ยิงทีละชุดละ CONCURRENCY แทนที่จะปล่อยพร้อมกันทั้งหมด แล้วสรุปผลครั้งเดียวตอนจบ
  const submitAll = async (batches) => {
    setSubmitting(true)

    let succeeded = 0

    try {
      for (const chunk of _.chunk(batches, CONCURRENCY)) {
        const results = await Promise.all(chunk.map((batch) => requestSubmit(batch).catch(() => false)))
        succeeded += results.filter(Boolean).length
      }
    } finally {
      // ไม่ว่าจะพังตรงไหน ปุ่มต้องหลุดจาก loading ไม่งั้นค้างถาวร
      setSubmitting(false)
    }

    if (succeeded === batches.length) {
      message.success(`ส่งคำขอสร้างใบเรียกเก็บ ${succeeded} ใบ ไปที่ lms เรียบร้อย`)
    } else {
      message.error(`สร้างสำเร็จ ${succeeded} จาก ${batches.length} ใบ กรุณาตรวจสอบใบที่ขาด`)
    }

    setIsModalOpen(false)
    await refetch().catch(() => {})
  }

  const requestOptions = async () => {
    const { partner_ids, lender_id } = form.getFieldsValue(true)
    const response = await jsonRequest('GET', `/admins/invoice/invoice_group?partner_ids=${partner_ids}&lender_id=${lender_id}`);

    let data = null;

    if (response.status === 200) {
      const json = await response.json();
      setSalaryDateOptions(json.salary_date_options || [])
      setEmploymentStatusOptions(json.employment_status_options || [])
      data = json;
    }
    setLoading(false)

    return data;
  }

  const onFinish = (values) => {
    const batches = values.report === 'seperate'
      ? values.partner_ids.map((partner_id) => ({ ...values, partner_ids: [partner_id] }))
      : [values]

    if (batches.length < CONFIRM_FROM) {
      submitAll(batches)
      return
    }

    Modal.confirm({
      title: 'ยืนยันการสร้างใบเรียกเก็บเงินแทน',
      content: `จะสร้าง ${batches.length} ใบ จาก ${values.partner_ids.length} บริษัท — ใบที่สร้างแล้วต้องไล่ยกเลิกทีละใบ`,
      okText: `สร้าง ${batches.length} ใบ`,
      cancelText: 'ย้อนกลับไปตรวจสอบ',
      onOk: () => submitAll(batches),
    })
  };

  const submitLabel = () => {
    if (loading) return 'กำลังโหลดข้อมูล โปรดรอสักครู่'
    if (submitting) return 'กำลังสร้างเอกสาร...'

    return 'ยืนยัน'
  }

  // ปุ่มยืนยันเป็นสีเทาจนกว่าจะกรอกครบทุกช่องบังคับ และ salary_dates / employment_statuses โหลดเสร็จ
  const isReady = () => {
    const {
      lender_id, report, type = [], partner_ids = [], salary_dates = [], employment_statuses = [],
      from_date, to_date, as_of_date
    } = form.getFieldsValue(true)

    if (loading || !lender_id || !report || type.length === 0) return false
    if (partner_ids.length === 0 || salary_dates.length === 0 || employment_statuses.length === 0) return false
    if (type.includes('payday') && (!from_date || !to_date)) return false
    if (type.includes('other') && !as_of_date) return false

    return !form.getFieldsError().some(({ errors }) => errors.length > 0)
  }

  const filterOption = (input, option) => (
    _.toString(option.children).toLowerCase().indexOf(input.toLowerCase()) >= 0
  )

  return (
    <Form form={form} name="control-hooks" onFinish={onFinish} layout="vertical"
      initialValues={{ type: [], partner_ids: [], report: 'seperate' }}
    >
      <Row gutter={24}>
        <Col xs={24} md={10}>
          <ColumnTitle>1. เลือกกลุ่มที่ต้องการเรียกเก็บ</ColumnTitle>

          <Form.Item name="lender_id" label="Lender" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={filterOption}
              placeholder="Select a option and change input text above"
              onChange={() => {resetPartners(); resetOptions(); onChange();}}
              loading={loading}
            >
              {lenders.map(({ id, name }) => (
                <Option value={id} key={`lender-${id}`}>{name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="partner_ids" label="Partner"
            rules={[{ required: true, type: 'array', min: 1, message: 'กรุณาเลือกอย่างน้อย 1 บริษัท' }]}
          >
            <PartnerPicker partners={partners} loading={loading} height={420} onChange={() => onChange()} />
          </Form.Item>

        </Col>

        <DividedColumn xs={24} md={7}>
          <ColumnTitle>2. เงื่อนไขพนักงาน</ColumnTitle>

          <Form.Item name="salary_dates" label="Salary dates"
            rules={[{ required: true, type: 'array', min: 1, message: 'กรุณาเลือกอย่างน้อย 1 รอบเงินเดือน' }]}
          >
            <ValuePicker
              options={salaryDateOptions}
              partners={partners}
              loading={loading}
              height={280}
              emptyText="เลือกพาร์ทเนอร์ก่อน"
            />
          </Form.Item>

          <Form.Item name="employment_statuses" label="Employment statuses"
            rules={[{ required: true, type: 'array', min: 1, message: 'กรุณาเลือกอย่างน้อย 1 สถานะ' }]}
          >
            <ValuePicker
              options={employmentStatusOptions}
              partners={partners}
              loading={loading}
              height={200}
              emptyText="เลือกพาร์ทเนอร์ก่อน"
            />
          </Form.Item>

        </DividedColumn>

        <DividedColumn xs={24} md={7}>
          <ColumnTitle>3. ประเภทและรอบของรายงาน</ColumnTitle>

          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Checkbox.Group >
              <Checkbox value="payday">Payday</Checkbox>
              <Checkbox value="other">Other</Checkbox>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item name="report" label="รวม report ตามบริษัทที่เลือก" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="seperate">แยก Report</Radio.Button>
              <Radio.Button value="append">รวม Report</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item  shouldUpdate>
            {
              () => { return (
                <>
                  {
                    form.getFieldValue('type').includes('payday') &&
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="วันเริ่มเบิก PAYDAY" name="from_date" rules={[{ required: true }]}>
                      <DatePicker style={{ width: '100%' }} disabledDate={disabledFutureDate} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="วันสิ้นสุดเบิก PAYDAY" name="to_date"
                      dependencies={['from_date']}
                      rules={[
                        { required: true },
                        ({ getFieldValue }) => ({
                          validator: (_rule, value) => {
                            const from = getFieldValue('from_date')

                            return !value || !from || !value.isBefore(from)
                              ? Promise.resolve()
                              : Promise.reject(new Error('ต้องไม่ก่อนวันเริ่มเบิก'))
                          }
                        })
                      ]}
                    >
                      <DatePicker style={{ width: '100%' }} disabledDate={disabledFutureDate} />
                    </Form.Item>
                  </Col>
                </Row>
                  }
                  {
                    form.getFieldValue('type').includes('other') &&
                <Form.Item label="วันตัดรอบ" name="as_of_date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} disabledDate={disabledFutureDate} />
                </Form.Item>
                  }
                  {
                    form.getFieldValue('type').length === 0 &&
                <EmptyHint>เลือก Type ด้านบนเพื่อระบุช่วงวันที่ของรายงาน</EmptyHint>
                  }
                </>
              )
              }
            }
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {
              () => {
                const { partner_ids = [], salary_dates = [], employment_statuses = [], type = [], report } =
                  form.getFieldsValue(true)
                const documents = report === 'seperate' ? partner_ids.length : Math.min(partner_ids.length, 1)

                return (
                  <SummaryBox>
                    <SummaryRow><span>บริษัท</span><span>{`${partner_ids.length} บริษัท`}</span></SummaryRow>
                    <SummaryRow><span>รอบเงินเดือน</span><span>{`${salary_dates.length} ตัวเลือก`}</span></SummaryRow>
                    <SummaryRow><span>สถานะพนักงาน</span><span>{`${employment_statuses.length} ตัวเลือก`}</span></SummaryRow>
                    <SummaryRow><span>ประเภท</span><span>{type.join(' + ') || '-'}</span></SummaryRow>
                    <SummaryRow $total><span>จะสร้างเอกสาร</span><span>{`${documents} ใบ`}</span></SummaryRow>
                  </SummaryBox>
                )
              }
            }
          </Form.Item>
        </DividedColumn>
      </Row>

      <FooterRow>
        <Form.Item shouldUpdate noStyle>
          {
            () => (
              <Button type="primary" htmlType="submit" size="large"
                loading={loading || submitting}
                disabled={!isReady()}
              >
                {submitLabel()}
              </Button>
            )
          }
        </Form.Item>
      </FooterRow>
    </Form>
  );
}

ReportForm.propTypes = {
  lenders: PropTypes.arrayOf(PropTypes.PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    partners: PropTypes.arrayOf(PropTypes.PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      version: PropTypes.number,
      partner_group: PropTypes.string,
      statement_date: PropTypes.number,
    })),
  })),
}

export default ReportForm
